import type { TFunction } from "i18next";
import type { AnalysisStatus, AnalysisVersionResponse } from "@/api/types";

/** Highest-version AnalysisVersion (current). */
export function pickLatest(
  versions: AnalysisVersionResponse[],
): AnalysisVersionResponse | null {
  if (versions.length === 0) return null;
  return [...versions].sort((a, b) => b.version - a.version)[0]!;
}

/** UI_PLAN §4.2 — terminal statuses ⇒ rerun button visible. */
export const TERMINAL_STATUSES: ReadonlySet<AnalysisStatus> = new Set([
  "done",
  "partial",
  "error",
]);

export function isTerminal(version: AnalysisVersionResponse | null): boolean {
  if (version === null) return false;
  return TERMINAL_STATUSES.has(version.status);
}

/** i18n key suffix (`transcribe` / `llm` / `consensus`) for the
 * "Возобновить с фазы N" button label, or `null` when the version cannot
 * usefully be resumed.
 *
 * ADR-015 §Skip semantics:
 *   - error_phase 3 ⇒ orchestrator restarts at phase 3 (transcribe).
 *   - error_phase 4 ⇒ orchestrator restarts at phase 4 (LLM fan-out).
 *   - error_phase 5 OR partial (consensus failure) ⇒ phase 5 path
 *     (delegated to retry-consensus when candidates were preserved; the
 *     UI label stays "Consensus" either way).
 *
 * Phase 1/2 failures have nothing the orchestrator can skip with, so the
 * resume button is hidden — operators use «Переанализировать» instead.
 * The backend re-validates and returns 409 if the artifacts disagree.
 */
export function resumeLabelKeyFor(
  version: AnalysisVersionResponse | null,
): "transcribe" | "llm" | "consensus" | null {
  if (version === null) return null;
  if (version.status !== "error" && version.status !== "partial") return null;
  // Partial is by construction a consensus failure (ADR-003) — error_phase
  // can be null on older rows, so we fall through to the consensus label
  // rather than hiding the button.
  if (version.status === "partial") return "consensus";
  const phase = version.error_phase;
  if (phase === 3) return "transcribe";
  if (phase === 4) return "llm";
  if (phase === 5) return "consensus";
  return null;
}

/**
 * Backend `ModelId` values returned in `snapshot_models` (see
 * src/recognition/api/schemas.py — LLMModelName). Kept in lock-step with the
 * i18n keys below so a typo in either place becomes a compile error.
 */
const MODEL_LABEL_KEY: Record<string, string> = {
  claude_opus: "meetings.card.version.modelClaudeOpus",
  openai_gpt: "meetings.card.version.modelOpenaiGpt",
  deepseek: "meetings.card.version.modelDeepseek",
};

/** Short token used when we don't have a translation for a snapshot model id
 * (e.g. backend ships a new provider before the FE locales are updated). */
function modelShortToken(modelId: string): string {
  // claude_opus → claude · openai_gpt → openai · deepseek → deepseek
  return modelId.split("_")[0] ?? modelId;
}

/** Localised label for one `ModelId` (e.g. "Claude Opus" / "OpenAI GPT"). */
export function modelLabel(modelId: string, t: TFunction): string {
  const key = MODEL_LABEL_KEY[modelId];
  if (key !== undefined) return t(key);
  // Unknown provider — fall back to the raw id so operators still see
  // *something* meaningful.
  return modelId;
}

/**
 * Human-readable model summary for the version picker / versions tab:
 *
 *   - empty snapshot          ⇒ em-dash (rows pre-v1.1.x or fixture noise)
 *   - exactly one model       ⇒ that model's localised name
 *   - two models w/ consensus ⇒ "claude+openai" (short tokens, no spaces)
 *   - N ≥ 3 with consensus    ⇒ "consensus (N=3)" (localised)
 *
 * The 1-vs-many split mirrors the backend rule: a single-model run never
 * enters phase 5, so showing a "consensus(…)" badge for it would be a lie.
 */
export function versionModelLabel(
  version: AnalysisVersionResponse,
  t: TFunction,
): string {
  const models = version.snapshot_models;
  if (models.length === 0) return t("common.dash");
  if (models.length === 1) return modelLabel(models[0]!, t);
  if (models.length === 2) {
    return models.map(modelShortToken).join("+");
  }
  return t("meetings.card.version.modelConsensus", { count: models.length });
}

/**
 * Compact local timestamp for the version picker: `dd.MM HH:mm`.
 *
 * Why not `toLocaleString("ru")`? — that produces `19.05.2026, 20:54:00`,
 * too long for an option-cell and noisy with the year/seconds. The picker
 * lists versions of *one* meeting so the year is redundant.
 */
export function versionDateLabel(version: AnalysisVersionResponse): string {
  const date = new Date(version.created_at);
  if (Number.isNaN(date.getTime())) return "";
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const hh = String(date.getHours()).padStart(2, "0");
  const mi = String(date.getMinutes()).padStart(2, "0");
  return `${dd}.${mm} ${hh}:${mi}`;
}

/**
 * Is `candidate` the latest version in `all`? Used by both the header
 * picker and the versions tab to flag the «(актуальная)» badge.
 */
export function isLatestVersion(
  candidate: AnalysisVersionResponse,
  all: AnalysisVersionResponse[],
): boolean {
  const latest = pickLatest(all);
  return latest !== null && latest.id === candidate.id;
}
